// Game state types. The sim runs on both client (for prediction) and server
// (authoritative). All entity arrays are typed with explicit shapes so the
// state can be cheaply cloned, serialized, and replicated.

export interface Tank {
  id: number;
  owner: number; // player slot 0-7
  alive: boolean;
  x: number; // world coords (0..65535), 256 world-units per tile
  y: number;
  dir: number; // 0..255, 0=N clockwise
  speed: number; // current speed in world-units/tick
  reload: number; // ticks remaining before next shell can fire
  armour: number; // 0..40
  shells: number; // 0..40
  mines: number; // 0..40
  trees: number; // 0..40
  // Pillboxes the tank is carrying after picking up destroyed pills off the
  // map. Consumed when the LGM places a fresh pill.
  carriedPills: number;
  // Tracks how long the player has been holding turn keys, for ramp-up
  // (WinBolo halves turn rate for the first 10 ticks of a turn).
  turnHoldTicks: number;
  // Slide state — used by shell knockback only (wall bumps don't arm a
  // slide; they just halt the blocked axis). slideTicks > 0 means apply
  // slideDist world-units along slideDir per tick (collision-checked) until
  // the timer reaches 0.
  slideTicks: number;
  slideDir: number; // 0..255 — same convention as Tank.dir
  slideDist: number; // world-units per slide step
  // True for one tick after a movement attempt where at least one axis was
  // blocked by an impassable tile. Read by the autopilot brain to trigger
  // wall-stuck recovery (A*) and fire-through-walls. Mirrors WinBolo
  // tank.c:1493/1505/1511/1514 which all set tank->obstructed = TRUE on
  // any failed axis check.
  obstructed: boolean;
  // Ticks accumulated while standing in River (shallow water). On overflow
  // past TANK_WATER_TICKS, the tank loses 1 shell and 1 mine.
  waterCount: number;
  // Gunsight range in half-tiles, GUNSIGHT_MIN..GUNSIGHT_MAX. Sets the next
  // shell's TTL on fire so it lands at the chosen range.
  sightLen: number;
  // Cooldown so MoreRange / LessRange ramp at GUNSIGHT_STEP_TICKS rate
  // instead of every tick when held.
  sightLenTimer: number;
  // Scoring: persistent across deaths within a session AND across rejoins
  // within the rejoin window (server revives the same tank entity in place
  // rather than spawning a fresh one).
  kills: number;
  deaths: number;
  // Per-session counters that feed the persistent player-stats blob on
  // permanent leave (rejoin window expiry). `pillsBuilt` increments when
  // the LGM completes a Pillbox action (fresh pill placed AND on-map-pickup
  // repairs); `basesCaptured` increments when the tank's owner takes a
  // base from a hostile/neutral owner. Cumulative across deaths AND across
  // rejoins within a session.
  pillsBuilt: number;
  basesCaptured: number;
  // Tank id of whoever last hit us with a shell — read on death to credit
  // the kill. 0 = unattributed (mine, drown, suicide). Reset after each death.
  lastDamageByTankId: number;
  // Spawn metadata for respawn.
  startIdx: number;
  // While > 0, this tank is riding a boat (id). The boat handles position;
  // the tank's own movement / shooting / mine-drop are suppressed.
  inBoatId: number;
  // The Little Green Man (LGM). Lives in the tank by default; exits to
  // perform build actions; parachutes back in after death.
  lgm: LGM;
  // Server-side build-command queue. When a build command arrives but the
  // LGM is still busy (WalkingTo/Building/WalkingHome), it lands here and
  // retries every tick until the LGM is InTank again — fixes the "first
  // click does nothing" race where the client's snapshot of lgmStatus is up
  // to one snapshot-interval stale and a brief mismatch silently drops the
  // input. Newer commands overwrite older; expires after `ticksLeft` so a
  // never-fillable click can't haunt the queue forever.
  pendingBuild: { action: number; targetX: number; targetY: number; ticksLeft: number } | null;
}

export interface Boat {
  id: number;
  riderTankId: number;
  x: number;
  y: number;
  dir: number; // 0..255, 0=N clockwise — direction of travel
  speed: number; // world-units/tick — driver-controlled (Faster/Slower keys)
  // No armour — WinBolo doesn't model boat HP. Shells go straight through
  // to the rider via applyShellHitTank, which ejects them and drops the
  // boat in one hit. (See damage.ts.)
}

export const LGMStatus = {
  InTank: 0,
  WalkingTo: 1,
  Building: 2,
  WalkingHome: 3,
  Dead: 4, // transient — killLGM() flips this to Parachuting on the same tick
  Parachuting: 5,
} as const;

export type LGMStatusCode = (typeof LGMStatus)[keyof typeof LGMStatus];

export interface LGM {
  status: LGMStatusCode;
  // World coords while outside the tank; meaningless when InTank/Dead.
  x: number;
  y: number;
  // Current build job. Cleared when complete or aborted.
  job: LGMJob | null;
  // Tick countdown for the Building state.
  buildTimer: number;
  // "Blessed" target tile (WinBolo lgm.c blessX/blessY). Set when the LGM
  // exits the tank for Road/Building/Boat/Pillbox actions; while the LGM is on
  // this exact tile, it counts as walkable for him even when the underlying
  // terrain has a 0 man-speed (e.g. just finished a Building → he's
  // standing on his own wall). Cleared the tick he steps off the tile or
  // returns to the tank. -1 = unset. Without this the WalkingHome → speed=0
  // branch would silent-teleport him back into the tank the tick after
  // finishing a wall, which the player sees as "my LGM disappeared".
  blessX: number;
  blessY: number;
}

export interface LGMJob {
  action: number; // BuildAction code
  targetX: number; // tile coords
  targetY: number;
  // Trees committed at reservation time (for the Pillbox repair action, which
  // consumes trees up-front so the resource is locked while the LGM walks).
  // 0 / unset for actions that don't need it.
  treesCommitted?: number;
}

export interface Shell {
  id: number;
  ownerTankId: number;
  /** Player slot of the entity that fired this shell. For tank-fired
   *  shells this is the firing tank's owner; for pillbox-fired shells it
   *  is the pillbox's owner. NEUTRAL_OWNER (0xff) for shells with no
   *  player ownership. Used by the base-impact path to decide whether
   *  the shell can damage + stop on a given base — friendly + neutral
   *  bases pass shells through (mirrors WinBolo `basesCanHit`). Not
   *  serialised to the client; server-only. */
  ownerSlot: number;
  x: number;
  y: number;
  dir: number;
  ttl: number; // ticks remaining before despawn
  maxTtl: number; // ttl at fire time — used for self-hit grace and pillbox cap
  /** Once a shell hits something (or expires), it isn't removed immediately.
   *  Removing it the same tick it spawned would mean close-range impacts
   *  (shell destroyed within the 5-tick snapshot window) never appear in any
   *  snapshot — the client would hear the cannon but never see the shell.
   *  Instead we mark the shell "dying" with a SNAPSHOT_INTERVAL_TICKS-long
   *  countdown; tickShells skips dying shells (no advance, no re-impact) and
   *  prunes when the countdown expires. Mirrors WinBolo's `packSent` flag
   *  ([shells.h:80, shells.c:199-202]) — a dead shell stays alive in the
   *  list until the network packet has carried it. */
  dyingTicks?: number;
}

// Visual + audit trail of a tank that just blew up. Big explosions damage
// terrain on the tick they spawn; the entity itself only carries the visual
// duration so clients can animate it. The `kind` flag drives renderer choice.
export interface TankExplosion {
  id: number;
  x: number; // world coords (tank's last position)
  y: number;
  kind: 'big' | 'small';
  ttl: number; // ticks remaining; 0 = despawn this tick
}

export interface SoundFx {
  id: number;
  kind: import('@bolo2k/shared').SoundEventKind;
  x: number; // world coords
  y: number;
}

export interface Pillbox {
  id: number;
  owner: number; // 0..15 owned, 0xFF neutral
  x: number; // tile coords (0..255)
  y: number;
  armour: number; // 0..15 (PILL_MAX_HEALTH)
  speed: number; // current gap between shots, in ticks; 100 quiescent, 6 floor
  fireCooldown: number; // ticks remaining until next shot
  cooldownDecayTimer: number; // ticks until next +1 to `speed` (relax back)
  // Two-tick target confirmation: pill must spot a hostile target on the
  // tick it's ready to fire AND have already spotted one on the previous
  // ready-to-fire tick. Mirrors WinBolo `justSeen` (pillbox.c:356-365);
  // gives a tank emerging from cover one fire-cycle of grace before the
  // pill actually shoots.
  justSeen: boolean;
  // 0 = on the map at (x, y). Otherwise the tank.id carrying this pill,
  // in which case the entity stays in state.pillboxes (preserving
  // ownership + identity for display + death-drop) but does not render
  // in-world or fire. Mirrors WinBolo's `inTank` flag (pillbox.h).
  carriedBy: number;
}

export interface Base {
  id: number;
  owner: number; // 0..7 owned, 0xFF neutral
  x: number; // tile coords (0..255)
  y: number;
  armour: number; // 0..90
  shells: number;
  mines: number;
  // Tick counters for stock regen + per-resource refuel cadence. Each one
  // counts down per tick; on hitting 0, the base does its action and resets
  // to its interval. See bases.ts for the cadence per [Cheshire] Maps&BrainsFAQ.
  regenTimer: number;
  refuelArmourTimer: number;
  refuelShellsTimer: number;
  refuelMinesTimer: number;
}

export interface StartPosition {
  x: number; // tile coords
  y: number;
  dir: number; // BMAP convention (0..15, 0=East CCW); converted on use
}

export interface GameState {
  tick: number;
  // Row-major 256x256, bytes encoded with TERRAIN_MASK low + 0x80 mine bit
  // (see @bolo2k/shared/terrain).
  terrain: Uint8Array;
  tanks: Tank[];
  shells: Shell[];
  pillboxes: Pillbox[];
  bases: Base[];
  boats: Boat[];
  starts: StartPosition[];
  tankExplosions: TankExplosion[];
  // One-shot sound triggers emitted by sim modules during a tick. The server
  // forwards these in the next snapshot and the array is cleared after each
  // broadcast (so each event fires exactly once on the client). See
  // `emitSound()` in this package; protocol kinds in shared/protocol.ts.
  soundEvents: SoundFx[];
  // Monotonic id counter for shells (and other transient entities).
  nextEntityId: number;
  // Per-player alliance request bitmaps. Index = player owner slot
  // (0..MAX_PLAYERS-1); value = bitmap of slots that player has requested
  // an alliance with. An *active* alliance requires both bits set (mutual
  // pact). Length = MAX_PLAYERS. Stored as Uint16Array because MAX_PLAYERS
  // is 16 — Cheshire's `Brain.h:170` PlayerBitMap is u_long (32-bit), so
  // 16-bit is sufficient and matches the original's design intent. See
  // `alliance.ts` for the helpers that read these.
  playerWantAllies: Uint16Array;
  // Game-type code: 1=Open, 2=Tournament, 3=StrictTournament. Drives the
  // per-spawn loadout in loader.ts/spawnTank and damage.ts/respawnTank.
  // [Cheshire] Brain.h GameType_open/tournament/strict_tment.
  gameType: number;
  // When true, mines laid in this game are filtered out of snapshot terrain
  // deltas for non-owner, non-allied viewers. Independent of gameType (an
  // Open game can be hidden-mines, a Tournament can be visible-mines).
  // [WinBolo] servermain.c default is TRUE; [Cheshire] GameFAQ "Who gets to
  // see mines I lay?" presupposes the hidden default.
  hiddenMines: boolean;
  // Per-tile metadata for player-laid mines, keyed by terrain index
  // (y*MAP_SIZE + x). Populated by mines.ts/tryDropMine; consulted by the
  // hidden-mine snapshot filter. BMAP-placed mines have no entry — those
  // are treated as visible to all (neutral map hazards, not player-laid).
  // Stale entries (where the underlying mine bit has been cleared) are
  // harmless: the filter checks the live terrain byte first.
  //
  // `revealedTo` is a PlayerBitMap of slots that have permanently learnt
  // about this mine via the proximity exception ([Cheshire] GameFAQ "If
  // you drop a mine out of the back of the tank, and there is an enemy
  // close to you, then they see it"). Once a bit is set, it stays set.
  mineMeta: Map<number, { owner: number; tickLaid: number; revealedTo: number }>;
  // Tick when each currently-active mutual-pact alliance formed. Keyed by
  // `"min:max"` so the pair is symmetric. Set by alliance.ts/requestAlly
  // when the pact becomes mutual; cleared by cancelAlly / clearAlliesFor
  // when it dissolves. Used by the mine-visibility rule to enforce
  // Cheshire's "mines laid *while* allied" semantics.
  allianceFormedAt: Map<string, number>;
}
